import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Section from './shared/Section';
import ContactInfo from './shared/ContactInfo';
import Button from './shared/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSend, FiMapPin, FiCheckCircle, FiAlertCircle } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    projectType: 'Web Development',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create FormData object for Netlify
      const netlifyFormData = new FormData();
      netlifyFormData.append('form-name', 'contact');
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        netlifyFormData.append(key, formData[key]);
      });

      const response = await fetch('/', {
        method: 'POST',
        body: netlifyFormData
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          projectType: 'Web Development',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section
      id="contact"
      title="Let's talk"
      subtitle="Ready to start your next project? Get in touch and let's create something amazing together."
      background="dark"
      className="min-h-screen pt-32"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Success Message */}
          {submitStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg flex items-center space-x-3"
            >
              <SafeIcon icon={FiCheckCircle} className="text-green-400 flex-shrink-0" />
              <div>
                <h4 className="text-green-400 font-semibold">Message Sent Successfully!</h4>
                <p className="text-green-300 text-sm">We'll get back to you within 24 hours.</p>
              </div>
            </motion.div>
          )}

          {/* Error Message */}
          {submitStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center space-x-3"
            >
              <SafeIcon icon={FiAlertCircle} className="text-red-400 flex-shrink-0" />
              <div>
                <h4 className="text-red-400 font-semibold">Message Failed to Send</h4>
                <p className="text-red-300 text-sm">Please try again or contact us directly.</p>
              </div>
            </motion.div>
          )}

          <form 
            name="contact" 
            method="POST" 
            data-netlify="true" 
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Hidden field for Netlify */}
            <input type="hidden" name="form-name" value="contact" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-slate-300 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-white placeholder-slate-400 transition-colors duration-300"
                  placeholder="John"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-slate-300 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-white placeholder-slate-400 transition-colors duration-300"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-white placeholder-slate-400 transition-colors duration-300"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label htmlFor="projectType" className="block text-sm font-medium text-slate-300 mb-2">
                Project Type
              </label>
              <select
                id="projectType"
                name="projectType"
                value={formData.projectType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-white transition-colors duration-300"
              >
                <option value="Web Development">Web Development</option>
                <option value="Mobile App">Mobile App</option>
                <option value="Digital Marketing">Digital Marketing</option>
                <option value="Brand Design">Brand Design</option>
                <option value="E-commerce">E-commerce</option>
                <option value="Consulting">Consulting</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                rows={6}
                value={formData.message}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-white placeholder-slate-400 transition-colors duration-300 resize-vertical"
                placeholder="Tell us about your project..."
              />
            </div>

            {/* Netlify reCAPTCHA */}
            {/* <div data-netlify-recaptcha="true" className="flex justify-center"></div> */}

            <Button
              variant="primary"
              size="lg"
              icon={FiSend}
              className="w-full"
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {isSubmitting ? 'Sending Message...' : 'Send Message'}
            </Button>

            <p className="text-slate-400 text-sm text-center">
              * Required fields. We'll respond within 24 hours.
            </p>
          </form>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="space-y-8"
        >
          <div>
            <h3 className="text-2xl font-bold mb-6 text-white">Get in touch</h3>
            <p className="text-slate-300 leading-relaxed mb-8">
              We'd love to hear about your project and discuss how we can help bring your vision to life. Whether you need a complete digital transformation or just want to improve your existing presence, we're here to help.
            </p>
          </div>

          <ContactInfo />

          {/* Map */}
          {/* <div className="bg-slate-800 rounded-lg overflow-hidden border border-slate-700">
            <div className="p-4 border-b border-slate-700">
              <h4 className="font-semibold text-white">Visit Our Office</h4>
              <p className="text-white text-sm">59 Walton St, Aylesbury HP21 7QG</p>
            </div>
            <div className="relative">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2456.789123456789!2d-0.8134567890123456!3d51.81234567890123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4876b5d5e5e5e5e5%3A0x5e5e5e5e5e5e5e5e!2s59%20Walton%20St%2C%20Aylesbury%20HP21%207QG%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890123"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="filter brightness-75 hover:brightness-100 transition-all duration-300"
                title="WeDesignedIt Office Location"
              />
              <div className="absolute top-4 right-4">
                <a
                  href="https://www.google.com/maps/place/59+Walton+St,+Aylesbury+HP21+7QG,+UK"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-300 flex items-center space-x-2"
                >
                  <SafeIcon icon={FiMapPin} className="text-xs" />
                  <span>Open in Maps</span>
                </a>
              </div>
            </div>
          </div> */}

          {/* Emergency Contact */}
          <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
            <h4 className="font-semibold mb-2 text-white">Need urgent help?</h4>
            <p className="text-slate-300 text-sm mb-3">
              For existing clients with urgent technical issues, we offer 24/7 emergency support.
            </p>
            <a
              href="tel:+447769061291"
              className="text-orange-400 hover:text-orange-300 font-medium text-sm transition-colors duration-300"
            >
              Contact Emergency Support →
            </a>
          </div>

          {/* Response Time Guarantee */}
          <div className="bg-gradient-to-r from-orange-500/20 to-orange-600/20 p-6 rounded-lg border border-orange-500/30">
            <div className="flex items-center space-x-3 mb-3">
              <SafeIcon icon={FiCheckCircle} className="text-orange-400 text-xl" />
              <h4 className="font-semibold text-white">Response Guarantee</h4>
            </div>
            <p className="text-slate-300 text-sm">
              We guarantee a response to all enquiries within 24 hours during business days, usually much sooner.
            </p>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

export default Contact;